import {
  BottomSheetBackdrop,
  BottomSheetBackdropProps,
  BottomSheetHandle,
  BottomSheetHandleProps,
  BottomSheetModal,
  BottomSheetProps,
  BottomSheetScrollView,
  BottomSheetView,
  useBottomSheetSpringConfigs
} from '@gorhom/bottom-sheet';
import { BottomSheetScrollViewProps } from '@gorhom/bottom-sheet/lib/typescript/components/bottomSheetScrollable/types';
import { BottomSheetViewProps } from '@gorhom/bottom-sheet/lib/typescript/components/bottomSheetView/types';
import { FocusScope } from '@tamagui/focus-scope';
import { useSheetController } from '@tamagui/sheet';
import { ScrollableProvider } from '@unistocks/components/basic/scrollview';
import { useMaxDynamicContentSize } from '@unistocks/components/sheet/use-max-dynamic-content-size';
import { useSheetContainerStyles } from '@unistocks/components/sheet/utils';
import { quicker } from '@unistocks/shared/animations';
import {
  OVERLAY_OPACITY,
  SHEET_SIZE_MD,
  SHEET_SIZE_SM,
  SHEET_SNAP_POINTS
} from '@unistocks/shared/consts';
import { useBackHandler } from '@unistocks/shared/hooks/use-backhandler';
import { useFocusEffect } from 'expo-router';
import React, { useCallback, useEffect, useRef } from 'react';
import { StyleSheet, ViewStyle } from 'react-native';
import { useReanimatedKeyboardAnimation } from 'react-native-keyboard-controller';
import Animated, {
  Extrapolation,
  interpolate,
  useAnimatedRef,
  useAnimatedStyle
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
  getVariableValue,
  isWeb,
  ProvideAdaptContext,
  useAdaptContext,
  useComposedRefs,
  useMedia,
  useTheme,
  ViewProps
} from 'tamagui';
import { useModalContext } from '../../providers/modal-context';
import { SheetHeader, SheetHeaderProps } from './sheet-header';

const Size = {
  small: SHEET_SIZE_SM,
  medium: SHEET_SIZE_MD
} as const;

export type SheetProps = Omit<BottomSheetProps, 'snapPoints' | 'footerComponent' | 'style'> & {
  ref?: React.Ref<BottomSheetModal>;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * Size of the sheet on medium and larger screens. On smaller screens, the sheet will always be full screen.
   * @default 'medium'
   */
  size?: keyof typeof Size;
  headerTitle?: string;
  headerSubtitle?: string;
  headerIcon?: React.ReactNode;
  headerRight?: React.ReactNode;
  headerProps?: ViewProps;
  /**
   * Set scroll mode of the sheet
   * - 'scrollView': the sheet content is wrapped in a ScrollView, and the sheet scrolls with the content. This is the default mode.
   * - 'view': the sheet content is wrapped in a BottomSheetView, and the sheet does not scroll with the content.
   * - 'custom': the sheet content must be wrapped in a custom component provided by the user e.g. VirtualList
   * @default 'scrollView'
   */
  scrollMode?: 'scrollView' | 'view' | 'custom';
  snapPoints?: (string | number)[];
  /**
   * Show handle
   * @default true
   */
  showHandle?: boolean;
  /**
   * Show overlay
   * @default true
   */
  showOverlay?: boolean;
  contentTopMargin?: boolean;
  scrollViewProps?: Omit<BottomSheetScrollViewProps, 'children'>;
  /**
   * Content container style when scrollMode is 'view'. If `scrollMode` is 'scrollView', use `scrollViewProps.contentContainerStyle` instead.
   */
  contentContainerStyle?: BottomSheetViewProps['style'];
  /**
   * Style props for the outer sheet component
   */
  style?: ViewStyle;
};

export const Sheet: React.FC<SheetProps> = ({
  ref,
  open: openProp,
  onOpenChange: onOpenChangeProp,
  headerTitle,
  headerSubtitle,
  headerIcon,
  headerRight,
  headerProps,
  scrollMode = 'scrollView',
  contentTopMargin = false,
  scrollViewProps,
  showHandle = true,
  showOverlay = true,
  enableDynamicSizing = false,
  size = 'medium',
  maxDynamicContentSize: maxDynamicContentSizeProp,
  snapPoints: snapPointsProp,
  containerStyle,
  contentContainerStyle,
  children,
  style: styleProp,
  ...props
}) => {
  // Integrate with Tamagui's SheetController context (provided by Popover/Dialog via Adapt)
  const { controller, isHidden } = useSheetController();
  // Capture Adapt context before the BottomSheetModal portal boundary
  // so we can re-provide it inside the portal for Adapt.Contents to work
  const adaptContext = useAdaptContext();
  const open = openProp ?? controller?.open;
  const onOpenChange = useCallback(
    (value: boolean) => {
      onOpenChangeProp?.(value);
      controller?.onOpenChange?.(value);
    },
    [onOpenChangeProp, controller]
  );

  const bottomSheetModalRef = useRef<BottomSheetModal>(null);
  const composedRef = useComposedRefs(ref, bottomSheetModalRef);
  const scrollRef = useAnimatedRef<Animated.ScrollView>();
  const { id: sheetId, isFocused } = useModalContext(open ?? false);

  // true while the sheet is in the @gorhom stack (between present() and onDismiss())
  const isSheetActiveRef = useRef(false);
  // true when open became true while a dismiss animation was still running
  const pendingPresentRef = useRef(false);

  const maxDynamicContentSize = useMaxDynamicContentSize(maxDynamicContentSizeProp);
  const contentWrapperStyle = useSheetContainerStyles(contentTopMargin);
  const { bottom: safeAreaBottom, top: safeAreaTop } = useSafeAreaInsets();
  const media = useMedia();
  const theme = useTheme();
  const { progress, height: keyboardHeight } = useReanimatedKeyboardAnimation();
  const animationConfig = useBottomSheetSpringConfigs(quicker);
  const size5 = getVariableValue('$5', 'size');
  const radius6 = getVariableValue('$6', 'radius');

  const snapPoints = enableDynamicSizing ? undefined : (snapPointsProp ?? SHEET_SNAP_POINTS);

  const keyboardOffset = useAnimatedStyle(() => ({
    marginBottom: interpolate(
      progress.value,
      [0, 1],
      [0, -keyboardHeight.value - safeAreaBottom],
      Extrapolation.CLAMP
    )
  }));

  const style = StyleSheet.create({
    handleIndicator: {
      backgroundColor: theme.unistocks.get(),
      width: size5
    },
    background: {
      backgroundColor: theme.background.get(),
      borderTopLeftRadius: radius6,
      borderTopRightRadius: radius6
    },
    sheet: {
      borderTopLeftRadius: radius6,
      borderTopRightRadius: radius6,
      borderColor: theme.borderColor.get(),
      borderWidth: 1
    }
  });

  useBackHandler(
    useCallback(() => {
      if (open) {
        bottomSheetModalRef.current?.dismiss();
        onOpenChange?.(false);

        return true;
      }

      return false;
    }, [open, onOpenChange])
  );

  useEffect(() => {
    if (!isWeb || !open || !isFocused) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        bottomSheetModalRef.current?.dismiss();
        onOpenChange?.(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onOpenChange, sheetId, isFocused]);

  useEffect(() => {
    if (open) {
      if (isSheetActiveRef.current) {
        // Dismiss animation still running — queue present for after it finishes
        pendingPresentRef.current = true;
      } else {
        pendingPresentRef.current = false;
        isSheetActiveRef.current = true;
        bottomSheetModalRef.current?.present();
      }
    } else {
      pendingPresentRef.current = false;
      if (isSheetActiveRef.current) {
        // Only dismiss when the sheet is actually in the stack
        bottomSheetModalRef.current?.dismiss();
      }
      // Skip when not active — prevents a redundant dismiss() after onDismiss already
      // fired, which would leave @gorhom internal state briefly inconsistent and block
      // the next present() call.
    }
  }, [open, sheetId]);

  const onDismiss = useCallback(() => {
    isSheetActiveRef.current = false;

    if (pendingPresentRef.current) {
      // open turned true while the dismiss animation was running — reopen immediately
      // without propagating false to the parent (state is already true)
      pendingPresentRef.current = false;
      isSheetActiveRef.current = true;
      bottomSheetModalRef.current?.present();
      return;
    }

    onOpenChange?.(false);
  }, [onOpenChange]);

  const renderBackdrop = useCallback(
    (props: BottomSheetBackdropProps) => {
      return (
        <BottomSheetBackdrop
          {...props}
          appearsOnIndex={0}
          disappearsOnIndex={-1}
          style={isWeb ? { position: 'fixed' } : undefined}
          opacity={OVERLAY_OPACITY}
          onPress={onDismiss}
        />
      );
    },
    [onDismiss]
  );

  const renderHandle = (props: BottomSheetHandleProps) => {
    return (
      <>
        {showHandle ? <BottomSheetHandle {...props} /> : null}
        {renderHeader({ mx: '$0', mt: showHandle ? undefined : '$4' })}
      </>
    );
  };

  const renderHeader = (overrideProps?: Partial<SheetHeaderProps>) =>
    headerTitle || headerIcon || headerRight ? (
      <SheetHeader
        title={headerTitle}
        subtitle={headerSubtitle}
        icon={headerIcon}
        iconRight={headerRight}
        {...overrideProps}
        {...headerProps}
      />
    ) : null;

  const renderContent = () => {
    let content;

    switch (scrollMode) {
      case 'scrollView':
        content = (
          <FocusScope loop trapped enabled={isFocused}>
            <BottomSheetScrollView
              ref={scrollRef}
              keyboardShouldPersistTaps={'handled'}
              // TODO: rn bug https://github.com/facebook/react-native/pull/51174
              // fadingEdgeLength={20}
              showsVerticalScrollIndicator={false}
              focusHook={useFocusEffect as any}
              {...scrollViewProps}
              contentContainerStyle={[contentWrapperStyle, scrollViewProps?.contentContainerStyle]}
            >
              <ScrollableProvider scrollableRef={scrollRef}>{children}</ScrollableProvider>
            </BottomSheetScrollView>
          </FocusScope>
        );
        break;

      case 'view':
        content = (
          <FocusScope loop trapped enabled={isFocused}>
            <BottomSheetView
              focusHook={useFocusEffect as any}
              style={[contentWrapperStyle, contentContainerStyle]}
            >
              {children}
            </BottomSheetView>
          </FocusScope>
        );
        break;

      case 'custom':
        content = children;
        break;
    }

    return content;
  };

  // When used inside Adapt and the breakpoint is not active, don't render
  if (isHidden) {
    return null;
  }

  return (
    <BottomSheetModal
      ref={composedRef}
      name={sheetId}
      enableBlurKeyboardOnGesture
      enableOverDrag
      enableDismissOnClose
      enableDynamicSizing={enableDynamicSizing}
      maxDynamicContentSize={maxDynamicContentSize}
      snapPoints={snapPoints}
      stackBehavior="push"
      animationConfigs={animationConfig}
      topInset={safeAreaTop}
      backdropComponent={showOverlay ? renderBackdrop : undefined}
      handleIndicatorStyle={style.handleIndicator}
      handleComponent={renderHandle}
      backgroundStyle={style.background}
      onDismiss={onDismiss}
      containerStyle={[
        {
          position: isWeb ? 'fixed' : 'absolute',
          width: media.sm ? '80%' : undefined,
          maxWidth: media.sm ? Size[size] : undefined,
          marginHorizontal: 'auto'
        },
        containerStyle
      ]}
      style={[style.sheet, styleProp]}
      {...props}
    >
      {adaptContext?.Contents != null ? (
        <ProvideAdaptContext {...adaptContext}>
          {renderContent()}
          <Animated.View style={keyboardOffset} />
        </ProvideAdaptContext>
      ) : (
        <>
          {renderContent()}
          <Animated.View style={keyboardOffset} />
        </>
      )}
    </BottomSheetModal>
  );
};

Sheet.displayName = 'Sheet';
